import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { toJS } from 'mobx';

import {
    Locales,
    formatDollars,
    PageBehaviourButton,
    ProductCardTabFooter,
    DefaultIntlFormatValues,
    EqualWidthMessageColumns,
    PRODUCT_CARD_HORIZONTAL_PADDING_PERCENTAGE,
} from '@ratehub/base-ui';
import { DEPOSITS_VERTICALS, trackHeapEventForDepositsAndNavigate } from '@ratehub/inv-common';

import SavingsAccountPropType from '../definitions/SavingsAccountPropType';


function SavingsAccountFooter({
    product,
    id,
    className,
}) {
    const intl = useIntl();

    const tabBar = [
        { title: <FormattedMessage {...LOCAL_MESSAGES.features} /> },
        { title: <FormattedMessage {...LOCAL_MESSAGES.details} /> },
    ];

    const featuresItems = getItemsForFeaturesTab(product);
    const tabPanelContent = [
        (
            <FeaturesTabContent
                key="features"
                items={featuresItems}
            />
        ),
        (
            <StyledEqualWidthMessageColumns
                key="savings-details"
                columns={getColumnsFromSavingsAccount({ product, intl })}
                // a wider column width required for FR (this is a very content-specific choice for optimal formatting)
                minimumColumnWidth={intl.locale === Locales.FRENCH
                    ? '15em'
                    : undefined}
            />
        ),
    ];

    if (!featuresItems.length) {
        tabBar.shift();        // remove the first tab: Features
        tabPanelContent.shift();
    }


    return (
        <ProductCardTabFooter
            tabBar={tabBar}
            tabPanelContent={tabPanelContent}
            productId={id}
            className={className}
        />
    );
}

SavingsAccountFooter.propTypes = {
    id: PropTypes.string.isRequired,
    product: SavingsAccountPropType,
    className: PropTypes.string,
};

const StyledEqualWidthMessageColumns = styled(EqualWidthMessageColumns)`
    padding-left: ${PRODUCT_CARD_HORIZONTAL_PADDING_PERCENTAGE};
    padding-right: ${PRODUCT_CARD_HORIZONTAL_PADDING_PERCENTAGE};
`;

const LOCAL_MESSAGES = defineMessages({
    features: {
        id: 'web-components.edb.savings.footer-tabs.features',
        defaultMessage: 'Features',
    },
    details: {
        id: 'web-components.edb.savings.footer-tabs.details',
        defaultMessage: 'Details',
    },
});


function getItemsForFeaturesTab(product) {
    const {
        interestNotes,
        freeMonthlyTransactions,
        thirdPartyAtmFee,
        restrictions,
    } = product;

    // Must use a shallow copy because we are mutating the array
    const items = [ ...toJS(interestNotes) ];

    if (freeMonthlyTransactions) {
        items.push(freeMonthlyTransactions);
    }

    if (thirdPartyAtmFee) {
        items.push(
            <FormattedMessage
                id="web-components.savings.third-party-atm"
                defaultMessage="External ATM fee: <b>{thirdPartyAtmFee, number, CAD2}</b>"
                values={{
                    thirdPartyAtmFee,
                    ...DefaultIntlFormatValues,
                }}
            />,
        );
    }

    if (restrictions) {     // 'Eligibility' in the CMS
        items.push(restrictions);
    }

    return items;
}

function FeaturesTabContent({ items }) {
    if (!items.length) {
        return null;
    }

    return (
        <ul className="rh-text-s rh-m-0 rh-pt-1 rh-pb-0_5 rh-max-width-50">
            <For
                each="item"
                of={items}
                index="index"
            >
                <Choose>
                    {/* simple strings must use dangerouslySet; may include simple markup from CMS */}
                    <When condition={typeof item === 'string'}>
                        <li
                            className="rh-mb-0_5"
                            key={index}
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: item }}
                        />
                    </When>
                    {/* objects, like from <FormattedMessage /> come through here */}
                    <Otherwise>
                        <li
                            className="rh-mb-0_5"
                            key={index}
                        >
                            {item}
                        </li>
                    </Otherwise>
                </Choose>
            </For>
        </ul>
    );
}

FeaturesTabContent.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.oneOfType([ PropTypes.string, PropTypes.node ])
    ).isRequired,
};


function getColumnsFromSavingsAccount({ product, intl }) {
    const {
        interestCompoundTerm = null,
        interestPaidTerm,

        minimumBalance,
        waiveMonthlyFeeBalance,
        monthlyFee,

        isMonetized,
        isCDIC,
    } = product;

    // ICU message syntax does not like kabab-case as select keys,
    // which is why for `calculated`, 'other' is used instead of 'minimal-balance'
    return [
        <FormattedMessage
            key="a1"
            id="web-components.savings.footer.calc-and-paid"
            defaultMessage={`
{calculated, select,
    null {}
    daily {Interest calculated: <b>daily</b>{newline}}
    other {Interest calculated: <b>monthly</b>{newline}}
}
    Interest paid: <b>
{paid, select,
    monthly {monthly}
    annually {annually}
    other {}
}</b>`}
            values={{
                calculated: interestCompoundTerm,
                paid: interestPaidTerm,
                ...DefaultIntlFormatValues,
            }}
        />,

        <FormattedMessage
            key="a2"
            id="web-components.savings.footer.minimum-balance"
            defaultMessage={`Minimum balance: <b>{minimumBalance, number, CAD0}</b>
{minimumBalance, select,
    0 {}
    other {
        {feeWaived, select,
            null {{newline}(required)}
            other {{newline}to waive fee of {feeWaived}}
        }
    }
}`}
            values={{
                minimumBalance: waiveMonthlyFeeBalance || minimumBalance,
                feeWaived: waiveMonthlyFeeBalance > 0
                    ? formatDollars(monthlyFee, intl)
                    : null,
                ...DefaultIntlFormatValues,
            }}
        />,

        <>
            <FormattedMessage
                key="a3"
                id="web-components.savings.footer.insurance"
                defaultMessage="Insurance: <b>{isCDIC, select, true {CDIC eligible} other {provincially insured}}</b>"
                values={{
                    isCDIC,
                    ...DefaultIntlFormatValues,
                }}
            />
            <If condition={!isMonetized}>
                <FormattedMessage
                    id="web-components.savings.read-more-provider-link"
                    defaultMessage="<learnMoreElsewhere>Learn more on provider site</learnMoreElsewhere>"
                    values={{
                        learnMoreElsewhere: msg => (
                            <PageBehaviourButton
                                data-name="cta-click-sav-learn-more"
                                size="extra-small"
                                shouldLowerCaseMessage={false}
                                onClick={() =>
                                    trackHeapEventForDepositsAndNavigate({
                                        product,
                                        shouldNavigate: true,
                                        vertical: DEPOSITS_VERTICALS.SAVINGS,
                                    })
                                }
                                message={msg}
                            />
                        ),
                    }}
                />
            </If>
        </>,
    ];
}


export default SavingsAccountFooter;
